import { TimeSlot, WorkingDay } from '../WorkingHours.types';
import { MINUTES_PER_DAY } from '../workingHoursConstants';

type SlotSpan = Pick<TimeSlot, 'startMinutes' | 'endMinutes'>;

const RESERVE_SLOT_ID = '__carry_in_reserve__';

// endMinutes <= startMinutes means the slot runs past midnight; for same-day
// overlap purposes it occupies the rest of the day (the portion after midnight
// isn't tracked/blocked — matches TimeSlotBar's rendering convention).
export const getEffectiveEndMinutes = (slot: SlotSpan) =>
  slot.endMinutes <= slot.startMinutes ? MINUTES_PER_DAY : slot.endMinutes;

export const slotsOverlap = (a: SlotSpan, b: SlotSpan) =>
  a.startMinutes < getEffectiveEndMinutes(b) && getEffectiveEndMinutes(a) > b.startMinutes;

export const daySlotsFit = (slots: TimeSlot[], excludeSlotId: string | null, candidate: SlotSpan) =>
  slots.every(slot => slot.id === excludeSlotId || !slotsOverlap(slot, candidate));

// Largest free [start, end) interval that contains `point`, or null if `point` falls
// inside an existing slot. Used to clamp direct-manipulation (double-click/drag) creation.
export const freeBoundsForDay = (slots: TimeSlot[], point: number): [number, number] | null => {
  const sorted = [...slots].sort((a, b) => a.startMinutes - b.startMinutes);
  let lo = 0;
  let hi = MINUTES_PER_DAY;
  for (const slot of sorted) {
    const end = getEffectiveEndMinutes(slot);
    if (slot.startMinutes <= point && end > point) return null;
    if (end <= point) lo = Math.max(lo, end);
    if (slot.startMinutes >= point) {
      hi = Math.min(hi, slot.startMinutes);
      break;
    }
  }
  return [lo, hi];
};

// How many minutes of `day` are occupied at the start by the previous day's overnight
// shift(s) spilling over — 0 if the previous day is disabled or has no overnight slot.
export const getCarryInMinutes = (prevDay: WorkingDay | undefined): number => {
  if (!prevDay || !prevDay.enabled) return 0;
  return prevDay.slots.reduce(
    (max, slot) => (slot.endMinutes <= slot.startMinutes ? Math.max(max, slot.endMinutes) : max),
    0
  );
};

export const getCarryInMinutesForDay = (days: WorkingDay[], dayId: string): number => {
  const index = days.findIndex(d => d.id === dayId);
  if (index < 0) return 0;
  const prevDay = days[(index + days.length - 1) % days.length];
  return getCarryInMinutes(prevDay);
};

// Injects the carry-in reserve as a synthetic occupied slot at the start of the day, so
// existing overlap/free-space checks (daySlotsFit, freeBoundsForDay) block it for free.
export const withCarryInReserve = (slots: TimeSlot[], reserveMinutes: number): TimeSlot[] =>
  reserveMinutes > 0
    ? [{ id: RESERVE_SLOT_ID, startMinutes: 0, endMinutes: reserveMinutes }, ...slots]
    : slots;
